import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";

import { getContextType } from "@/lib/authorization";
import { ContextType } from "@/lib/authorization";
import { TENANT_ID_STORAGE_KEY } from "@/lib/contexts/Auth/AuthContext";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";
import { TokenPayload } from "@/types/response/LoginResponses";
import { UserRole } from "@/types/user/userType";

export interface AuthState {
  accessToken: string | null;
  role: UserRole | null;
  userId: string | null;
  tenantId: string | null;
  context: ContextType | null;
}

/**
 * Hook that reads auth state from localStorage.
 * Returns current authentication state including token, role, user ID, tenant ID, and context.
 *
 * @returns AuthState object with all auth-related state
 */
export function useAuthState(): AuthState {
  const [tenantId] = useLocalStorage(TENANT_ID_STORAGE_KEY, "");
  const [userId] = useLocalStorage("userId", "");
  const [accessToken] = useLocalStorage("accessToken", "");
  const [role] = useLocalStorage<UserRole | null>("role", null);

  const context = useMemo(() => {
    if (!accessToken) {
      return null;
    }

    try {
      const decodedToken: TokenPayload = jwtDecode(accessToken);
      return getContextType({ clientId: decodedToken.clientId });
    } catch {
      return null;
    }
  }, [accessToken]);

  return {
    accessToken,
    role,
    userId,
    tenantId,
    context,
  };
}
